'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';

/**
 * Figma 画框 1922×547（节点 1120:187）；卡片在设计稿 495 高的基础上从底部裁掉 165，横幅设计高度 382。
 *
 * 高度：宽屏时按宽度等比；宽度变窄时不再同比缩矮，而是按「设计稿高度 + 窄出来的宽度 × 0.12」反而略增高，
 * 最高不超过屏幕高度 32%（不低于 200px），矮屏仍给下方留出空间。
 * 横幅内的媒体元素按横幅「高度」等比缩放（h()），人物、水印不变形；标语按容器宽度响应式缩放，
 * 避免不同语言或窄屏下被拉伸、遮挡或裁切。
 */
const FRAME_W = 1922;
const FRAME_H = 382;
/** 卡片坐标系：背景图与人物都高 620、顶边在卡片上方 62px */
const MEDIA_TOP = -62;
const MEDIA_HEIGHT = 620;
const CARD_TOP = 52;
const CARD_HEIGHT = 330;
/** 人物右边缘距画框右边的距离：1922 − 336.61 − 1456.77 */
const FIGURE_RIGHT = FRAME_W - 336.61 - 1456.77;
/** 背景图实际宽度（3946×1240 的纯蓝渐变原图，按 620 高铺满时的宽） */
const BACKDROP_WIDTH = (3946 / 1240) * MEDIA_HEIGHT;

/** 设计稿像素 → 按横幅高度换算的长度（横幅高度存在 --bh 里） */
const h = (px: number) => `calc(var(--bh) * ${px / FRAME_H})`;

export function StudioHero({ compact = false }: { compact?: boolean }) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  if (compact) {
    return (
      <header className="mb-6 sm:mb-5">
        <h1 className="font-semibold text-[32px] text-system-white leading-[1.4] sm:text-2xl">{t('title')}</h1>
      </header>
    );
  }
  return (
    <header className="w-full [container-type:inline-size]">
      <div
        data-testid="studio-hero-stage"
        className="relative w-full overflow-hidden"
        style={{
          // 1440 宽时 = 286（与原来一致）；每窄 100px 增高约 12px；上限为屏幕高度 32%（不低于 200px）
          // 另外不超过横幅宽度 30%：再高的话人物会占满整行，标语没地方放
          ['--bh' as string]: 'min(calc(286px + (1440px - 100cqw) * 0.12), max(200px, 32vh), 30cqw)',
          height: 'var(--bh)',
        }}
      >
        <h1 className="sr-only">{t('title')}</h1>
        <div
          className="absolute inset-x-0 bottom-0 overflow-hidden bg-[#0175ae]"
          style={{ height: h(CARD_HEIGHT), borderRadius: h(40) }}
        >
          {/* 背景渐变按高度铺满、水平居中；卡片变宽时两侧露出的部分由同色底色补齐 */}
          <div
            aria-hidden="true"
            className="-translate-x-1/2 absolute left-1/2"
            style={{ top: h(MEDIA_TOP), width: h(BACKDROP_WIDTH), height: h(MEDIA_HEIGHT) }}
          >
            <Image
              src="/images/offline-digital-human/offline-avatar-banner-backdrop.webp"
              alt=""
              fill
              priority
              sizes="(max-width: 767px) 100vw, 1440px"
              className="object-cover"
            />
          </div>
          {/* 大字水印：DIN Alternate Bold，渐变描色；字体只有 Bold 一档，用同色描边再加粗一圈，整体 8% 透明度 */}
          <p
            aria-hidden="true"
            className="-translate-x-1/2 absolute whitespace-nowrap bg-gradient-to-t from-white to-white/40 bg-clip-text text-center font-black text-transparent leading-none opacity-[0.08] [font-family:'DIN_Alternate','DIN_Condensed','Bahnschrift',sans-serif]"
            style={{
              left: '50%',
              top: h(26),
              fontSize: h(196),
              letterSpacing: h(-4),
              WebkitTextStroke: `${h(4)} rgba(255,255,255,0.7)`,
            }}
          >
            OFFLINE AVATAR
          </p>
          {/* 标语按容器宽度响应式缩放，并置于人物上层，保证不同语言都完整可见。 */}
          <p
            className="-translate-x-1/2 absolute left-1/2 z-10 max-w-[calc(100%-32px)] whitespace-nowrap text-center font-normal text-[clamp(14px,1.45cqw,21px)] text-white leading-none tracking-[clamp(6px,1cqw,14px)] [font-family:'PingFang_SC',sans-serif] sm:hidden"
            style={{
              top: h(218),
            }}
          >
            {t('hero.tagline')}
          </p>
        </div>
        {/* 人物抠图：叠在卡片与文字之上、只受外框裁切，头部探出卡片顶边，底部与卡片齐平，贴右 */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            right: h(FIGURE_RIGHT),
            top: h(CARD_TOP + MEDIA_TOP),
            width: h(1456.77),
            height: h(MEDIA_HEIGHT),
          }}
        >
          <Image
            src="/images/offline-digital-human/offline-avatar-banner-fg.webp"
            alt=""
            fill
            priority
            sizes="(max-width: 767px) 100vw, 1440px"
            className="object-cover"
          />
        </div>
      </div>
    </header>
  );
}
